import MailOutline from "@mui/icons-material/MailOutline";
import { Stack, Typography } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { DataGridPro } from "@mui/x-data-grid-pro";
import type { FC, ReactElement } from "react";
import { useMemo } from "react";
import { MomentFormCreateFollowUpButton } from "../../../../modules/moment/component/buttons/moment-form-create-follow-up-button/moment-form-create-follow-up-button";
import type { GroupedMomentAnalyticsData } from "../../../../modules/stats/server/analytics-service";
import { filterStatType } from "../../../../modules/stats/server/analytics-service";
import type { ISO8601 } from "../../../../utils/iso8601";
import type { UUID } from "../../../../utils/uuid";
import { AnalyticsDatetime } from "../../utils/analytics-datetime";
import { EmptyState } from "../generic/empty-state";
import { ExportToolbar } from "../generic/export-toolbar";
import { calculatePercentage } from "../utils";
import { AnalyticsSendsTableMissingOpens } from "./missing-opens";

export const AnalyticsSendsTableExpandedOpens: FC<{
  momentId: UUID;
  stats: GroupedMomentAnalyticsData["stats"];
  isWhatsApp?: boolean;
}> = ({ momentId, stats, isWhatsApp = false }) => {
  const { data, overall, missing } = useMemo(() => {
    const opens = filterStatType("grouped_open", stats).map((x) => x.data);
    const deliveries = filterStatType("delivery", stats);

    return {
      data: opens,
      missing: deliveries.filter((delivery) => {
        return !opens.some((open) => open.recipient_data?.id === delivery.data.recipient_data?.id);
      }),
      overall: {
        total: deliveries.length,
        count: opens.length,
      },
    };
  }, [stats]);

  const rows = useMemo(() => {
    return data.map((item, id) => {
      return {
        id,
        first_name: item.recipient_data?.first_name ?? "-",
        last_name: item.recipient_data?.last_name ?? "-",
        first_open: item.min_timestamp,
        most_recent_open: item.max_timestamp,
        work_email: item.recipient_data?.email ?? "-",
        delivery_address: item.recipient_data?.display ?? "-",
      };
    });
  }, [data]);

  const columns = useMemo(() => {
    const renderDatetime = (
      params: GridRenderCellParams<(typeof rows)[0], ISO8601>,
    ): ReactElement | null =>
      params.value ? (
        <Stack direction="row" spacing={1} alignItems="center" height="100%">
          <AnalyticsDatetime datetime={params.value} />
        </Stack>
      ) : null;

    const columnData: GridColDef<(typeof rows)[0]>[] = [
      {
        field: "first_name",
        headerName: "First Name",
        flex: 1,
      },
      {
        field: "last_name",
        headerName: "Last Name",
        flex: 1,
      },
    ];

    if (isWhatsApp) {
      columnData.push({
        field: "first_open",
        headerName: "Read",
        flex: 2,
        renderCell: renderDatetime,
      });
    } else {
      columnData.push(
        {
          field: "first_open",
          headerName: "First Open",
          flex: 1.5,
          renderCell: renderDatetime,
        },
        {
          field: "most_recent_open",
          headerName: "Most Recent Open",
          flex: 1.5,
          renderCell: renderDatetime,
        },
        {
          field: "work_email",
          headerName: "Work Email",
          flex: 1,
        },
        {
          field: "delivery_address",
          headerName: "Delivery Address",
          flex: 1,
        },
      );
    }

    return columnData;
  }, [isWhatsApp]);

  const percent = calculatePercentage(overall.count, overall.total);
  const recipientLabel = overall.count === 1 ? "Recipient" : "Recipients";

  if (!rows.length)
    return (
      <EmptyState
        symbol={<MailOutline fontSize="large" />}
        title={isWhatsApp ? "No reads yet" : "No opens yet"}
        subtitle="Check back soon"
      />
    );

  return (
    <Stack spacing={2} direction="column">
      <Stack direction="row" spacing={2} alignItems="flex-end">
        <Typography variant="h4">{`${percent}%`}</Typography>
        <Typography variant="body1" color="text.secondary">
          ({overall.count} {isWhatsApp ? recipientLabel.toLowerCase() : recipientLabel})
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        These numbers only count people who received the {isWhatsApp ? "WhatsApp message" : "email"}
        .
      </Typography>
      {!isWhatsApp && <MomentFormCreateFollowUpButton momentId={momentId} isRecurring={false} />}
      <DataGridPro
        pagination
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        slots={{ toolbar: ExportToolbar }}
        showToolbar
      />
      <AnalyticsSendsTableMissingOpens
        deliveries={missing.map((x) => x.data)}
        isWhatsApp={isWhatsApp}
      />
    </Stack>
  );
};
